import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Pencil, Plus, RefreshCw, Save, Trash2, X } from 'lucide-react'
import { api, getApiErrorMessage } from '../lib/api'
import InlineError from '../ui/InlineError'
import Loading from '../ui/Loading'

const SUB_TABS = [
  { id: 'topics', label: '주제 관리' },
  { id: 'statistics', label: '통계' },
]

const STATUS_LABELS = { DRAFT: '임시저장', PUBLISHED: '게시', ARCHIVED: '보관' }

function unwrap(response) {
  return response?.data?.data ?? response?.data ?? null
}

function dateText(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function currentWeek() {
  const today = new Date()
  const day = today.getDay() || 7
  const monday = new Date(today)
  monday.setDate(today.getDate() - day + 1)
  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  return { startsOn: dateText(monday), endsOn: dateText(sunday) }
}

function clone(value) {
  return JSON.parse(JSON.stringify(value))
}

function emptyDraft(catalog) {
  const week = currentWeek()
  const first = catalog?.[0]
  return {
    title: '',
    summary: '',
    ...week,
    status: 'DRAFT',
    priority: 0,
    ruleMode: 'ANY',
    rules: first ? [{ ruleCode: first.code, config: clone(first.config || {}) }] : [],
  }
}

export default function OpsCustomerWorkHubManagementPage() {
  const [activeTab, setActiveTab] = useState('topics')
  const [topics, setTopics] = useState([])
  const [catalog, setCatalog] = useState([])
  const [statistics, setStatistics] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [draft, setDraft] = useState(null)
  const [saving, setSaving] = useState(false)

  const catalogByCode = useMemo(
    () => new Map(catalog.map((rule) => [rule.code, rule])),
    [catalog],
  )

  const loadTopics = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = unwrap(await api.get('/ops/customer-work-hub', { __silent: true })) || {}
      setTopics(Array.isArray(data.topics) ? data.topics : [])
      setCatalog(Array.isArray(data.ruleCatalog) ? data.ruleCatalog : [])
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, '고객업무허브 관리 정보를 불러오지 못했습니다.'))
    } finally {
      setLoading(false)
    }
  }, [])

  const loadStatistics = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setStatistics(unwrap(await api.get('/ops/customer-work-hub/statistics', { __silent: true })) || {})
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, '고객업무허브 통계를 불러오지 못했습니다.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadTopics() }, [loadTopics])
  useEffect(() => {
    if (activeTab === 'statistics') loadStatistics()
  }, [activeTab, loadStatistics])

  const startCreate = () => {
    setEditingId(null)
    setDraft(emptyDraft(catalog))
    setError('')
    setMessage('')
  }

  const startEdit = (topic) => {
    setEditingId(topic.id)
    setDraft({
      title: topic.title || '',
      summary: topic.summary || '',
      startsOn: topic.startsOn || '',
      endsOn: topic.endsOn || '',
      status: topic.status || 'DRAFT',
      priority: Number(topic.priority || 0),
      ruleMode: topic.ruleMode || 'ANY',
      rules: clone(topic.rules || []),
    })
    setError('')
    setMessage('')
  }

  const save = async () => {
    if (!draft) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      if (editingId) await api.put(`/ops/customer-work-hub/topics/${editingId}`, draft)
      else await api.post('/ops/customer-work-hub/topics', draft)
      setMessage(editingId ? '추천 주제를 수정했습니다.' : '추천 주제를 생성했습니다.')
      setDraft(null)
      setEditingId(null)
      await loadTopics()
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, '추천 주제를 저장하지 못했습니다.'))
    } finally {
      setSaving(false)
    }
  }

  const remove = async (topic) => {
    if (!window.confirm(`'${topic.title}' 주제를 삭제할까요?`)) return
    setError('')
    try {
      await api.delete(`/ops/customer-work-hub/topics/${topic.id}`)
      setMessage('추천 주제를 삭제했습니다.')
      await loadTopics()
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, '추천 주제를 삭제하지 못했습니다.'))
    }
  }

  return (
    <section className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-5">
        <div className="text-sm text-slate-500">운영자 · 기타관리</div>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900">고객업무허브관리</h1>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200">
        <div className="flex" role="tablist" aria-label="고객업무허브관리">
          {SUB_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`border-b-2 px-4 py-2 text-sm font-semibold ${activeTab === tab.id ? 'border-brand-sky text-brand-navy' : 'border-transparent text-slate-500'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          title="새로고침"
          aria-label="새로고침"
          onClick={activeTab === 'topics' ? loadTopics : loadStatistics}
          className="mb-1 inline-flex h-9 w-9 items-center justify-center rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {error ? <InlineError message={error} className="mb-4 rounded border border-red-100 bg-red-50" /> : null}
      {message ? <div className="mb-4 text-sm font-medium text-emerald-700">{message}</div> : null}
      {loading ? <Loading /> : null}

      {!loading && activeTab === 'topics' ? (
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_440px]">
          <div className="min-w-0">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-900">추천 주제</h2>
              <button type="button" onClick={startCreate} className="inline-flex h-9 items-center gap-1.5 rounded bg-slate-800 px-3 text-sm font-semibold text-white">
                <Plus className="h-4 w-4" /> 주제 추가
              </button>
            </div>
            <div className="overflow-x-auto border border-slate-200 bg-white">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-xs text-slate-600">
                  <tr><th className="px-3 py-2">주제</th><th className="px-3 py-2">기간</th><th className="px-3 py-2">상태</th><th className="px-3 py-2">기준</th><th className="w-24 px-3 py-2">관리</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {topics.map((topic) => (
                    <tr key={topic.id}>
                      <td className="px-3 py-3"><div className="font-semibold text-slate-900">{topic.title}</div><div className="mt-0.5 max-w-md truncate text-xs text-slate-500">{topic.summary || '-'}</div></td>
                      <td className="whitespace-nowrap px-3 py-3 text-xs text-slate-600">{topic.startsOn}<br />{topic.endsOn}</td>
                      <td className="px-3 py-3"><span className="rounded bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">{STATUS_LABELS[topic.status] || topic.status}</span></td>
                      <td className="px-3 py-3 text-xs text-slate-600">{topic.ruleMode === 'ALL' ? '전체 충족' : '하나 이상'} · {(topic.rules || []).length}개</td>
                      <td className="px-3 py-3"><div className="flex gap-1"><button type="button" title="수정" onClick={() => startEdit(topic)} className="inline-flex h-8 w-8 items-center justify-center rounded border border-slate-300"><Pencil className="h-4 w-4" /></button><button type="button" title="삭제" disabled={topic.status === 'PUBLISHED'} onClick={() => remove(topic)} className="inline-flex h-8 w-8 items-center justify-center rounded border border-slate-300 text-red-600 disabled:opacity-30"><Trash2 className="h-4 w-4" /></button></div></td>
                    </tr>
                  ))}
                  {!topics.length ? <tr><td colSpan="5" className="px-4 py-12 text-center text-slate-500">등록된 추천 주제가 없습니다.</td></tr> : null}
                </tbody>
              </table>
            </div>
          </div>

          <TopicEditor
            draft={draft}
            editingId={editingId}
            catalog={catalog}
            catalogByCode={catalogByCode}
            saving={saving}
            onChange={setDraft}
            onSave={save}
            onClose={() => { setDraft(null); setEditingId(null) }}
          />
        </div>
      ) : null}

      {!loading && activeTab === 'statistics' ? <StatisticsPanel data={statistics || {}} /> : null}
    </section>
  )
}

function TopicEditor({ draft, editingId, catalog, catalogByCode, saving, onChange, onSave, onClose }) {
  if (!draft) return <aside className="border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">주제를 추가하거나 수정할 항목을 선택하세요.</aside>

  const patch = (values) => onChange({ ...draft, ...values })
  const updateRule = (index, next) => patch({ rules: draft.rules.map((rule, ruleIndex) => ruleIndex === index ? next : rule) })
  const addRule = () => {
    const first = catalog[0]
    if (first) patch({ rules: [...draft.rules, { ruleCode: first.code, config: clone(first.config || {}) }] })
  }

  return (
    <aside className="border border-slate-300 bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3"><h2 className="font-bold text-slate-900">{editingId ? '주제 수정' : '주제 추가'}</h2><button type="button" title="닫기" onClick={onClose}><X className="h-5 w-5" /></button></div>
      <div className="max-h-[68vh] space-y-4 overflow-y-auto p-4">
        <label className="block text-xs font-semibold text-slate-600">주제명<input value={draft.title} onChange={(event) => patch({ title: event.target.value })} maxLength={120} className="mt-1 h-10 w-full rounded border border-slate-300 px-3 text-sm" /></label>
        <label className="block text-xs font-semibold text-slate-600">안내 문구<textarea value={draft.summary} onChange={(event) => patch({ summary: event.target.value })} maxLength={500} rows={3} className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm" /></label>
        <div className="grid grid-cols-2 gap-2"><label className="text-xs font-semibold text-slate-600">시작일<input type="date" value={draft.startsOn} onChange={(event) => patch({ startsOn: event.target.value })} className="mt-1 h-10 w-full rounded border border-slate-300 px-2 text-sm" /></label><label className="text-xs font-semibold text-slate-600">종료일<input type="date" value={draft.endsOn} onChange={(event) => patch({ endsOn: event.target.value })} className="mt-1 h-10 w-full rounded border border-slate-300 px-2 text-sm" /></label></div>
        <div className="grid grid-cols-3 gap-2"><label className="text-xs font-semibold text-slate-600">상태<select value={draft.status} onChange={(event) => patch({ status: event.target.value })} className="mt-1 h-10 w-full rounded border border-slate-300 px-2 text-sm"><option value="DRAFT">임시저장</option><option value="PUBLISHED">게시</option><option value="ARCHIVED">보관</option></select></label><label className="text-xs font-semibold text-slate-600">기준 조합<select value={draft.ruleMode} onChange={(event) => patch({ ruleMode: event.target.value })} className="mt-1 h-10 w-full rounded border border-slate-300 px-2 text-sm"><option value="ANY">하나 이상</option><option value="ALL">전체 충족</option></select></label><label className="text-xs font-semibold text-slate-600">우선순위<input type="number" value={draft.priority} onChange={(event) => patch({ priority: Number(event.target.value) })} className="mt-1 h-10 w-full rounded border border-slate-300 px-2 text-sm" /></label></div>

        <div className="border-t border-slate-200 pt-3">
          <div className="mb-2 flex items-center justify-between"><span className="text-sm font-bold text-slate-900">조회 기준</span><button type="button" onClick={addRule} className="inline-flex h-8 items-center gap-1 rounded border border-slate-300 px-2 text-xs font-semibold"><Plus className="h-3.5 w-3.5" /> 기준 추가</button></div>
          <div className="space-y-3">
            {draft.rules.map((rule, index) => {
              const definition = catalogByCode.get(rule.ruleCode)
              return <RuleEditor key={`${index}-${rule.ruleCode}`} rule={rule} definition={definition} catalog={catalog} onChange={(next) => updateRule(index, next)} onRemove={() => patch({ rules: draft.rules.filter((_, ruleIndex) => ruleIndex !== index) })} />
            })}
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3"><button type="button" onClick={onClose} className="h-9 rounded border border-slate-300 px-3 text-sm font-semibold">취소</button><button type="button" disabled={saving} onClick={onSave} className="inline-flex h-9 items-center gap-1.5 rounded bg-brand-navy px-3 text-sm font-semibold text-white disabled:opacity-50"><Save className="h-4 w-4" />{saving ? '저장 중' : '저장'}</button></div>
    </aside>
  )
}

function RuleEditor({ rule, definition, catalog, onChange, onRemove }) {
  const config = rule.config || {}
  const setConfig = (values) => onChange({ ...rule, config: { ...config, ...values } })
  const changeDefinition = (code) => {
    const next = catalog.find((item) => item.code === code)
    onChange({ ruleCode: code, config: clone(next?.config || {}) })
  }
  return (
    <div className="border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center gap-2"><select value={rule.ruleCode} onChange={(event) => changeDefinition(event.target.value)} className="h-9 min-w-0 flex-1 rounded border border-slate-300 bg-white px-2 text-sm">{catalog.map((item) => <option key={item.code} value={item.code}>{item.category === 'CONTACT' ? '연락' : item.category === 'COVERAGE' ? '보장' : '기타'} · {item.label}</option>)}</select><button type="button" title="기준 삭제" onClick={onRemove} className="inline-flex h-9 w-9 items-center justify-center rounded border border-slate-300 text-red-600"><Trash2 className="h-4 w-4" /></button></div>
      <div className="mt-2 flex flex-wrap gap-2">
        {Object.prototype.hasOwnProperty.call(config, 'days') ? <SmallNumber label="기간" value={config.days} suffix="일" onChange={(value) => setConfig({ days: value })} /> : null}
        {Object.prototype.hasOwnProperty.call(config, 'leadDays') ? <SmallNumber label="앞으로" value={config.leadDays} suffix="일" onChange={(value) => setConfig({ leadDays: value })} /> : null}
        {Object.prototype.hasOwnProperty.call(config, 'includeFamily') ? <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700"><input type="checkbox" checked={Boolean(config.includeFamily)} onChange={(event) => setConfig({ includeFamily: event.target.checked })} className="accent-slate-600" />가족 포함</label> : null}
        {Object.prototype.hasOwnProperty.call(config, 'regionCode') ? <label className="text-xs font-semibold text-slate-600">지역<select value={config.regionCode || 'SEOUL'} onChange={(event) => setConfig({ regionCode: event.target.value })} className="ml-1 h-8 rounded border border-slate-300 bg-white px-2">{(definition?.regionOptions || []).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label> : null}
      </div>
      {(definition?.options || []).length ? <CoverageRuleConfig definition={definition} config={config} setConfig={setConfig} /> : null}
    </div>
  )
}

function CoverageRuleConfig({ definition, config, setConfig }) {
  const selectedCodes = Array.isArray(config.selectedCodes) ? config.selectedCodes : []
  return <div className="mt-2 space-y-2"><div className="flex flex-wrap gap-2"><label className="text-xs font-semibold text-slate-600">비교<select value={config.comparison || 'NONE'} onChange={(event) => setConfig({ comparison: event.target.value })} className="ml-1 h-8 rounded border border-slate-300 bg-white px-2">{(definition.comparisonOptions || []).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>{!['NONE', 'ANY'].includes(config.comparison) ? <SmallNumber label="기준" value={Math.round(Number(config.thresholdAmount || 0) / 10000)} suffix="만원" onChange={(value) => setConfig({ thresholdAmount: value * 10000 })} /> : null}</div><div className="flex flex-wrap gap-1.5">{definition.options.map((option) => { const selected = selectedCodes.includes(option.code); return <label key={option.code} className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-xs ${selected ? 'border-slate-400 bg-slate-200' : 'border-slate-300 bg-white'}`}><input type="checkbox" checked={selected} onChange={(event) => setConfig({ selectedCodes: event.target.checked ? [...new Set([...selectedCodes, option.code])] : selectedCodes.filter((code) => code !== option.code) })} className="accent-slate-600" />{option.label}</label> })}</div></div>
}

function SmallNumber({ label, value, suffix, onChange }) {
  return <label className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600">{label}<input type="number" min="0" value={value ?? 0} onChange={(event) => onChange(Number(event.target.value))} className="h-8 w-20 rounded border border-slate-300 bg-white px-2 text-right" />{suffix}</label>
}

function StatisticsPanel({ data }) {
  const rows = Array.isArray(data.topics) ? data.topics : []
  return <div><div className="mb-4 grid gap-3 sm:grid-cols-4"><Stat label="전체 주제" value={data.topicCount || 0} /><Stat label="게시 주제" value={data.publishedCount || 0} /><Stat label="노출 사용자" value={data.exposedUserCount || 0} /><Stat label="목록 조회 사용자" value={data.listViewUserCount || 0} /></div><div className="overflow-x-auto border border-slate-200 bg-white"><table className="min-w-full divide-y divide-slate-200 text-sm"><thead className="bg-slate-50 text-left text-xs text-slate-600"><tr><th className="px-3 py-2">주제</th><th className="px-3 py-2">기간</th><th className="px-3 py-2 text-right">노출 사용자</th><th className="px-3 py-2 text-right">목록 조회 사용자</th></tr></thead><tbody className="divide-y divide-slate-100">{rows.map((row) => <tr key={row.id}><td className="px-3 py-3 font-semibold text-slate-900">{row.title}</td><td className="px-3 py-3 text-xs text-slate-600">{row.starts_on} ~ {row.ends_on}</td><td className="px-3 py-3 text-right tabular-nums">{row.exposed_users || 0}</td><td className="px-3 py-3 text-right tabular-nums">{row.list_view_users || 0}</td></tr>)}</tbody></table></div></div>
}

function Stat({ label, value }) {
  return <div className="border border-slate-200 bg-white p-4"><div className="text-xs font-medium text-slate-500">{label}</div><div className="mt-1 text-2xl font-bold tabular-nums text-slate-900">{Number(value).toLocaleString()}</div></div>
}
